import type { IDirectory, IDirectoryListResponse } from "@/types/directoryTypes";
import { appConfigService } from "@/services/appConfigService";
import { authService } from "@/services/authService";

/**
 * Directory Service
 * Handles API calls for Azure AD directory/tenant operations.
 * The backend acquires the ARM token via OBO — the frontend only sends the standard API bearer token.
 */

async function getApiBaseUrl(): Promise<string> {
  const config = await appConfigService.getAppEnvironmentData();
  return config.apiBaseUrl;
}

/**
 * Creates headers with authentication token
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  await authService.createAuthProvider();
  const token = await authService.getAccessToken();

  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Get the list of available directories/tenants for the current user
 */
async function getDirectories(): Promise<IDirectory[]> {
  try {
    const baseUrl = await getApiBaseUrl();
    const headers = await getAuthHeaders();

    const response = await fetch(`${baseUrl}/directory/list`, {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch directories: ${response.statusText}`);
    }

    const data: IDirectoryListResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching directories:", error);
    throw error;
  }
}

export const directoryService = {
  getDirectories,
};
